import {Directive, DoCheck, Input} from '@angular/core';
import {Ride} from '../models/ride';
import {GoogleMapsAPIWrapper} from '@agm/core';
import {CoolLocalStorage} from '@angular-cool/storage';

declare var google: any;

@Directive({
  selector: 'agm-custom-waypoints'
})
export class DirectionsMapWaypoints implements DoCheck {
  @Input() ride: Ride;

  currentAddressArrayLength = 1;

  addressArrayHash = '';

  directionsService: any = undefined;
  directionsDisplay: any = undefined;
  legRenderers: any[] = [];
  drawRequestId = 0;

  static generateLocationHash(array: any) {
    return JSON.stringify(array);
  }

  constructor (private gmapsApi: GoogleMapsAPIWrapper, private localStorage: CoolLocalStorage) {

  }

  /**
   * Company route calculation setting ('shortest' | 'fastest'), delivered with the dashboard settings
   */
  routeCalcMethod(): string {
    try {
      const settings = JSON.parse(this.localStorage.getItem('dashboardSettings'));
      return settings.companySettings.routeCalcMethod;
    } catch (e) {
      return undefined;
    }
  }

  clearLegRenderers() {
    this.legRenderers.forEach(renderer => renderer.setMap(null));
    this.legRenderers = [];
  }

  /**
   * Google only offers route alternatives for requests without waypoints, so for the company's
   * 'shortest' setting every leg is requested on its own and drawn with its shortest alternative.
   */
  drawShortestRoute(fallbackOptions: any) {
    const self = this;
    const requestId = ++this.drawRequestId;
    const stops = this.ride.addressArray.filter((a: any) => typeof(a.lat) !== 'undefined');
    if (stops.length < 2) {
      return;
    }

    const requests = [];
    for (let i = 0; i < stops.length - 1; i++) {
      requests.push(new Promise<any>(resolve => {
        this.directionsService.route({
          origin: {lat: stops[i].lat, lng: stops[i].lng},
          destination: {lat: stops[i + 1].lat, lng: stops[i + 1].lng},
          travelMode: 'DRIVING',
          provideRouteAlternatives: true
        }, (response: any, status: any) => {
          if (status !== 'OK') {
            return resolve(null);
          }
          let routeIndex = 0;
          response.routes.forEach((route: any, index: number) => {
            if (route.legs[0].distance.value < response.routes[routeIndex].legs[0].distance.value) {
              routeIndex = index;
            }
          });
          resolve({response, routeIndex});
        });
      }));
    }

    Promise.all(requests).then(results => {
      if (requestId !== self.drawRequestId) {
        return;
      }
      if (results.some(r => r === null)) {
        // A leg could not be routed: keep map and price consistent with one plain request
        self.drawSingleRoute(fallbackOptions);
        return;
      }
      self.gmapsApi.getNativeMap().then(map => {
        if (requestId !== self.drawRequestId) {
          return;
        }
        self.directionsDisplay.setMap(null);
        self.clearLegRenderers();
        self.ride.distance = 0;
        self.ride.duration = 0;
        results.forEach(({response, routeIndex}) => {
          const renderer = new google.maps.DirectionsRenderer({map, suppressMarkers: true, preserveViewport: false});
          renderer.setDirections(response);
          renderer.setRouteIndex(routeIndex);
          self.legRenderers.push(renderer);
          const leg = response.routes[routeIndex].legs[0];
          self.ride.distance += leg.distance.value;
          self.ride.duration += leg.duration.value;
        });
      });
    });
  }

  drawSingleRoute(routeOptions: any) {
    const self = this;
    const requestId = ++this.drawRequestId;
    this.gmapsApi.getNativeMap().then(map => {
      if (requestId !== self.drawRequestId) {
        return;
      }
      self.clearLegRenderers();
      self.directionsDisplay.setMap(map);
      self.directionsDisplay.setOptions( { suppressMarkers: true } );
      self.directionsService.route(routeOptions, function(response: any, status: any) {
        if (status === 'OK' && requestId === self.drawRequestId) {
          self.directionsDisplay.setDirections(response);

          self.ride.distance = 0;
          self.ride.duration = 0;
          const legs = response.routes[0].legs;
          for (let i = 0; i < legs.length; ++i) {
            self.ride.distance += legs[i].distance.value;
            self.ride.duration += legs[i].duration.value;
          }
        } else if (status !== 'OK') {
          console.log('Waypoint: Directions request failed due to ' + status);
        }
      });
    });
  }

  drawRoute() {
    const self = this;
    if (typeof(google) === 'undefined' || !this.ride.addressArray) {
      setTimeout(() => { self.drawRoute(); }, 100);
      return false;
    }

    if (
      typeof(this.ride.addressArray[this.ride.addressArray.length - 1]) !== 'undefined' &&
      typeof(this.ride.addressArray[this.ride.addressArray.length - 1].lat) !== 'undefined' &&
      typeof(this.ride.addressArray[0].lat) !== 'undefined' &&
      typeof(this.ride.addressArray[1]) !== 'undefined'
    ) {
      if (typeof(this.directionsService) === 'undefined') {
        this.directionsService = new google.maps.DirectionsService;
      }

      if (typeof(this.directionsDisplay) === 'undefined') {
        this.directionsDisplay = new google.maps.DirectionsRenderer;
      }

      const waypointArray = [];
      if (this.ride.addressArray.length >= 2) {
        for (let i = 1; i < (this.ride.addressArray.length - 1); i++) {
          if (typeof(this.ride.addressArray[i].lat) !== 'undefined') {
            waypointArray.push({
              location: new google.maps.LatLng(this.ride.addressArray[i].lat, this.ride.addressArray[i].lng),
              stopover: false
            });
          }
        }

        this.currentAddressArrayLength = this.ride.addressArray.length;

        const self = this;

        const routeOptions: any = {
          origin: {
            lat: this.ride.addressArray[0].lat,
            lng: this.ride.addressArray[0].lng
          },
          destination: {
            lat: this.ride.addressArray[this.ride.addressArray.length - 1].lat,
            lng: this.ride.addressArray[this.ride.addressArray.length - 1].lng
          },
          travelMode: 'DRIVING'
        };

        if(waypointArray && waypointArray.length) {
          routeOptions.waypoints = waypointArray;
        }

        if (this.routeCalcMethod() === 'shortest') {
          this.drawShortestRoute(routeOptions);
        } else {
          this.drawSingleRoute(routeOptions);
        }
      }
    }
  }

  ngDoCheck(): void {
    const newAddressArrayHash = DirectionsMapWaypoints.generateLocationHash(this.ride.addressArray);
    if (this.addressArrayHash !== newAddressArrayHash) {
      this.addressArrayHash = newAddressArrayHash;
      // console.log(`[DirectionsMapWaypoints.ngDoCheck]: Address array changed, drawing new route`);
      this.drawRoute();
    }
  }
}
